import { i18n, type SupportedLanguage } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import type { AiAssistant } from "@/types/ai/Assistant";
import { Assistant } from "./Assistant";

/**
 * Discover Assistant - Helps users find datasets
 */
export class DiscoverAssistant extends Assistant {
  private language: SupportedLanguage;

  constructor() {
    super();
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: this.language,
    };
  }

  getSystemPrompt(): string {
    return `
You are an AI assistant that helps users find and explore datasets in Atlasio.

## YOUR ROLE
Help users discover datasets by:
- Understanding what data they're looking for
- Searching for relevant datasets
- Explaining what datasets are available
- Guiding them to browse categories

## AVAILABLE ACTIONS
You can help users navigate to discover pages and search for datasets. However, you cannot directly search or fetch datasets - you should guide users on how to use the discover interface.

## RESPONSE STYLE
- Be helpful and conversational
- Suggest relevant search terms or categories
- Guide users to the discover interface
- Always respond in the selected i18n language (see CRITICAL LANGUAGE REQUIREMENT above)

## EXAMPLES
User: "Find datasets about income"
→ Guide them to search for "income" in the discover page

User: "Show me population data"
→ Suggest searching for "population" or browsing relevant categories
    `;
  }

  async execute(prompt: string): Promise<void> {
    const { showNotification } = useAiNotificationStore.getState();

    const response = await Assistant.sendPrompt<void>(prompt, this.config());

    if (response.error) {
      showNotification(response.error, true);
      return;
    }

    if (response.reply) {
      showNotification(response.reply, false);
    }

    if (response.data) {
      // TODO: Implement discover logic
    }
  }
}
